import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
  Alert,
  Card,
  CardContent,
  Chip,
  Typography
} from '@mui/material';
import { useFilters } from '@/contexts/FilterContext';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';
import { ProductCell } from '@/components/ProductCell';
import { CopyableId } from '@/components/CopyableId';
import { useProductsMap } from '@/hooks/useProductsMap';
import { ProductScopeGate } from '@/components/ProductScopeGate';

interface CredentialRow {
  id?: string;
  keyPrefix?: string;
  status?: string;
  createdAt?: string;
  expiresAt?: string;
}

export const ProductBillingCredentialsTab: React.FC = () => {
  const { productId } = useFilters();
  const { can } = useAdminPermissions();
  const { showError, showSuccess } = useMessage();
  const productsById = useProductsMap();
  const [credentials, setCredentials] = useState<CredentialRow[]>([]);
  const [lastCreatedKey, setLastCreatedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = useCallback(async () => {
    if (!productId?.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiService.listBillingCredentials(productId);
      const data = res.data?.data ?? res.data?.items ?? res.data;
      setCredentials(Array.isArray(data) ? data : []);
    } catch (err) {
      setCredentials([]);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    setLastCreatedKey(null);
    load().catch(() => undefined);
  }, [load, productId]);

  const handleCreate = async () => {
    try {
      const res = await apiService.createBillingCredential(productId, {});
      const key = res.data?.apiKey || res.data?.key || res.data?.plainKey;
      if (key) setLastCreatedKey(String(key));
      showSuccess('Credencial criada');
      await load();
    } catch {
      showError('Erro ao criar credencial');
    }
  };

  const handleRotate = async () => {
    try {
      const res = await apiService.rotateBillingCredential(productId, {});
      const key = res.data?.apiKey || res.data?.key || res.data?.plainKey;
      if (key) setLastCreatedKey(String(key));
      showSuccess('Rotação iniciada (janela dual-key)');
      await load();
    } catch {
      showError('Erro na rotação');
    }
  };

  const handleRevokeNow = async (credentialId?: string) => {
    if (!credentialId) return;
    try {
      await apiService.revokeBillingCredential(productId, credentialId);
      showSuccess('Credencial revogada imediatamente');
      await load();
    } catch {
      showError('Erro ao revogar credencial');
    }
  };

  const handleDelete = async (credentialId?: string) => {
    if (!credentialId) return;
    try {
      await apiService.deleteBillingCredential(productId, credentialId);
      showSuccess('Credencial excluída');
      await load();
    } catch {
      showError('Erro ao excluir credencial');
    }
  };

  return (
    <ProductScopeGate
      productId={productId}
      integration="billing"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={load}
    >
      <Box>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Credenciais billing do produto
              </Typography>
              <ProductCell
                productId={productId}
                name={productsById.get(productId)?.displayName}
                avatarUrl={productsById.get(productId)?.avatarUrl}
              />
              <Alert severity="info">
                Credenciais <code>vb_live_...</code> para o vover-app chamar o billing. A API
                interna admin ↔ produto usa <code>urls.appApiSecret</code> no registry (não é a
                mesma chave).
              </Alert>
            </Stack>
          </CardContent>
        </Card>
        {lastCreatedKey && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Chave exibida uma vez — copie agora:{' '}
            <CopyableId value={lastCreatedKey} label="apiKey" />
          </Alert>
        )}
        {can('billing:write') && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="contained" onClick={handleCreate}>
              Nova credencial
            </Button>
            <Button variant="outlined" onClick={handleRotate}>
              Rotacionar
            </Button>
          </Stack>
        )}
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Prefixo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Alert severity="info" sx={{ my: 1 }}>
                      Nenhuma credencial billing cadastrada para este produto. A integração com o
                      vover-billing está acessível — crie a primeira credencial acima.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                credentials.map(row => (
                  <TableRow key={row.id || row.keyPrefix}>
                    <TableCell>
                      <CopyableId value={row.id || ''} label="id" />
                    </TableCell>
                    <TableCell>{row.keyPrefix || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status || 'unknown'} />
                    </TableCell>
                    <TableCell align="right">
                      {can('billing:write') && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => handleRevokeNow(row.id)}
                          >
                            Revogar
                          </Button>
                          <Button size="small" color="error" onClick={() => handleDelete(row.id)}>
                            Excluir
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </ProductScopeGate>
  );
};
